import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Plus, Search, Trash2, ChevronRight, ChevronLeft,
  Package, Database, ArrowLeft, FlaskConical, Award, ChevronDown, ChevronUp, Edit3, Layers, FileText
} from 'lucide-react';
import BrandProdukForm from '@/components/sample/BrandProdukForm';
import VariantSizeAccordion from '@/components/production/VariantSizeAccordion';
import AsaExportBrandPdfModal from '@/Components/production/AsaExportBrandPdfModal';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { useSession } from '@/lib/SessionContext';
import { getPageAccess, getShowPriceAccess } from '@/lib/AuthSession';

const SORT_OPTIONS = [
  { key: 'az', label: 'Brand: A → Z' },
  { key: 'za', label: 'Brand: Z → A' },
  { key: 'terbaru', label: 'Paling Baru Diupdate' },
];

function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find(s => s.key === value);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E6D5B8] bg-[#FFFDF9] text-xs font-semibold text-[#8C6208] hover:bg-[#F9F5EC] transition-all shadow-2xs"
      >
        {current?.label || 'Urutkan'}
        <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-[#E6D5B8] rounded-xl shadow-xl py-1 min-w-[180px] animate-in fade-in zoom-in-95 duration-100">
            {SORT_OPTIONS.map(opt => (
              <button key={opt.key} onClick={() => { onChange(opt.key); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between ${value === opt.key ? 'bg-[#F9F5EC] text-[#8C6208] font-black' : 'text-slate-700 hover:bg-[#F9F5EC]/50'}`}>
                <span>{opt.label}</span>
                {value === opt.key && <span className="w-1.5 h-1.5 rounded-full bg-[#8C6208]" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RowPerPageDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const options = [6, 12, 24, 48, 96];

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="h-8 px-3 text-[11px] font-bold text-[#8C6208] bg-[#FFFDF9] hover:bg-[#F9F5EC] border border-[#E6D5B8] rounded-xl flex items-center gap-2 shadow-2xs transition-all"
      >
        <span>{value}</span>
        <ChevronDown className={`w-3 h-3 text-[#8C6208] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full mb-1.5 left-0 z-30 bg-white border border-[#E6D5B8] rounded-xl shadow-xl py-1 min-w-[80px] overflow-hidden animate-in fade-in zoom-in-95 duration-100">
            {options.map(n => (
              <button
                key={n}
                type="button"
                onClick={() => { onChange(n); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs font-bold transition-colors flex items-center justify-between ${
                  value === n 
                    ? 'bg-[#F9F5EC] text-[#8C6208] font-black' 
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <span>{n}</span>
                {value === n && <span className="w-1.5 h-1.5 rounded-full bg-[#8C6208]" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function DatabaseBrand() {
  const qc = useQueryClient();
  const { member } = useSession();
  const canEdit = getPageAccess(member, "sample_racikan") === "edit";
  const location = useLocation();
  const isMarketingPortal = location.pathname.includes('/office/marketing');
  
  // Marketing sees price by default, others only if explicitly granted via Manajemen Tim
  const isMarketing = isMarketingPortal || getShowPriceAccess(member, "database_brand");

  const [renameBrand, setRenameBrand] = useState(null);
  const [newBrandName, setNewBrandName] = useState('');

  const handleRenameSubmit = () => {
    if (newBrandName && newBrandName.trim() !== "" && newBrandName.trim() !== renameBrand) {
      updateBrandNameMut.mutate({ oldName: renameBrand, newName: newBrandName.trim() }, {
        onSuccess: () => {
          setRenameBrand(null);
        }
      });
    }
  };

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('az');
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantSearch, setVariantSearch] = useState('');
  const [renameVariantModal, setRenameVariantModal] = useState(null); // { oldName: '', newName: '' }
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileRef = React.useRef(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  React.useEffect(() => { setCurrentPage(1); }, [search, sort]);

  // Forms state
  const [showBrandProdukForm, setShowBrandProdukForm] = useState(false);
  const [showAsaExportModal, setShowAsaExportModal] = useState(false);
  const [editRacikanItem, setEditRacikanItem] = useState(null);
  const [editBahanItem, setEditBahanItem] = useState(null);
  const [editDiscoverySetData, setEditDiscoverySetData] = useState(null);

  // Fetch Racikan Database
  const { data: racikanList = [], isLoading: loadingRacikan } = useQuery({
    queryKey: ['racikan-database'],
    queryFn: () => base44.entities.RacikanDatabase.list('nama_brand'),
  });

  // Fetch Database Bahan
  const { data: bahanList = [], isLoading: loadingBahan } = useQuery({
    queryKey: ['database-bahan'],
    queryFn: () => base44.entities.DatabaseBahan.list('nama_brand'),
  });

  // Fetch Master Inventories for Integration Check
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: masterBahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });

  const checkBahanInInventory = (r) => {
    if (r.dari_client || (r.vendor && String(r.vendor).toLowerCase() === 'client')) return true;
    const namaBahan = r.nama_bahan || r.bahan_nama || '';
    if (!namaBahan) return false;
    const normalize = (str) => (str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    let matched = masterBahanList.find(x => (x.nama || '').trim().toLowerCase() === namaBahan.trim().toLowerCase());
    if (!matched) {
      const rcNorm = normalize(namaBahan);
      matched = masterBahanList.find(x => normalize(x.nama) === rcNorm);
    }
    if (!matched) {
      const stdCategories = ['dpg', 'aqua des', 'alkohol', 'peg', 'sustain'];
      const catLower = (r.kategori_bahan || r.kategori || '').trim().toLowerCase();
      if (stdCategories.includes(catLower)) {
        matched = masterBahanList.find(x => (x.kategori || '').trim().toLowerCase() === catLower);
      }
    }
    return !!matched;
  };

  const checkPackagingInInventory = (type, name, isFullset, itemId) => {
    if (!name || name === '—' || name === '-') return false;
    if (name.toLowerCase().includes('dari client')) return true;

    const clean = name.trim().toLowerCase();
    const normalize = (str) => (str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    const normName = normalize(name);

    if (isFullset || type === 'fullset') {
      if (itemId && (fullsetList.some(x => x.id === itemId) || botolList.some(x => x.id === itemId))) return true;
      if (fullsetList.some(x => (x.nama || '').trim().toLowerCase() === clean)) return true;
      if (botolList.some(x => (x.nama || '').trim().toLowerCase() === clean)) return true;
      if (fullsetList.some(x => normalize(x.nama) === normName)) return true;
      if (botolList.some(x => normalize(x.nama) === normName)) return true;
      
      const rootName = clean.replace(/(\d+)\s*(ml|l|gr|g)?/gi, '').trim();
      if (rootName.length >= 4) {
        if (fullsetList.some(x => (x.nama || '').toLowerCase().includes(rootName))) return true;
        if (botolList.some(x => (x.nama || '').toLowerCase().includes(rootName))) return true;
      }
      return false;
    }
    if (type === 'botol') {
      if (itemId && (botolList.some(x => x.id === itemId) || fullsetList.some(x => x.id === itemId))) return true;
      if (botolList.some(x => (x.nama || '').trim().toLowerCase() === clean)) return true;
      if (fullsetList.some(x => (x.nama || '').trim().toLowerCase() === clean)) return true;
      if (botolList.some(x => normalize(x.nama) === normName)) return true;
      if (fullsetList.some(x => normalize(x.nama) === normName)) return true;

      const rootName = clean.replace(/(\d+)\s*(ml|l|gr|g)?/gi, '').trim();
      if (rootName.length >= 4) {
        if (botolList.some(x => (x.nama || '').toLowerCase().includes(rootName))) return true;
        if (fullsetList.some(x => (x.nama || '').toLowerCase().includes(rootName))) return true;
      }
      return false;
    }
    if (type === 'tutup') {
      if (itemId && tutupList.some(x => x.id === itemId)) return true;
      return tutupList.some(x => (x.nama || '').trim().toLowerCase() === clean || normalize(x.nama) === normName);
    }
    if (type === 'spray') {
      if (itemId && sprayList.some(x => x.id === itemId)) return true;
      return sprayList.some(x => (x.nama || '').trim().toLowerCase() === clean || normalize(x.nama) === normName);
    }
    if (type === 'item_tambahan') {
      if (itemId && itemTambahanList.some(x => x.id === itemId)) return true;
      return itemTambahanList.some(x => (x.nama || '').trim().toLowerCase() === clean || normalize(x.nama) === normName);
    }
    return false;
  };

  const getBrandMissingCount = (brand) => {
    let missingCount = 0;
    let totalItems = 0;
    brand.variants.forEach(v => {
      v.sizes.forEach(s => {
        if (s.racikan && Array.isArray(s.racikan.racikan)) {
          s.racikan.racikan.forEach(r => {
            totalItems++;
            if (!checkBahanInInventory(r)) missingCount++;
          });
        }
        if (s.bahan) {
          if (s.bahan.is_fullset) {
            if (s.bahan.fullset_nama && s.bahan.fullset_nama !== 'Dari Client') {
              totalItems++;
              if (!checkPackagingInInventory('fullset', s.bahan.fullset_nama, true)) missingCount++;
            }
          } else {
            if (s.bahan.botol_nama && s.bahan.botol_nama !== 'Dari Client') {
              totalItems++;
              if (!checkPackagingInInventory('botol', s.bahan.botol_nama, false)) missingCount++;
            }
            if (s.bahan.tutup_nama && s.bahan.tutup_nama !== 'Dari Client') {
              totalItems++;
              if (!checkPackagingInInventory('tutup', s.bahan.tutup_nama, false)) missingCount++;
            }
            if (s.bahan.spray_nama && s.bahan.spray_nama !== 'Dari Client') {
              totalItems++;
              if (!checkPackagingInInventory('spray', s.bahan.spray_nama, false)) missingCount++;
            }
          }
          if (Array.isArray(s.bahan.item_tambahan)) {
            s.bahan.item_tambahan.forEach(it => {
              if (it.nama && !it.dari_client) {
                totalItems++;
                if (!checkPackagingInInventory('item_tambahan', it.nama, false)) missingCount++;
              }
            });
          }
        }
      });
    });
    return { missingCount, totalItems };
  };

  const getVariantMissingCount = (variant) => {
    let missingCount = 0;
    let totalItems = 0;
    variant.sizes.forEach(s => {
      if (s.racikan && Array.isArray(s.racikan.racikan)) {
        s.racikan.racikan.forEach(r => {
          totalItems++;
          if (!checkBahanInInventory(r)) missingCount++;
        });
      }
      if (s.bahan) {
        if (s.bahan.is_fullset) {
          if (s.bahan.fullset_nama && s.bahan.fullset_nama !== 'Dari Client') {
            totalItems++;
            if (!checkPackagingInInventory('fullset', s.bahan.fullset_nama, true)) missingCount++;
          }
        } else {
          if (s.bahan.botol_nama && s.bahan.botol_nama !== 'Dari Client') {
            totalItems++;
            if (!checkPackagingInInventory('botol', s.bahan.botol_nama, false)) missingCount++;
          }
          if (s.bahan.tutup_nama && s.bahan.tutup_nama !== 'Dari Client') {
            totalItems++;
            if (!checkPackagingInInventory('tutup', s.bahan.tutup_nama, false)) missingCount++;
          }
          if (s.bahan.spray_nama && s.bahan.spray_nama !== 'Dari Client') {
            totalItems++;
            if (!checkPackagingInInventory('spray', s.bahan.spray_nama, false)) missingCount++;
          }
        }
        if (Array.isArray(s.bahan.item_tambahan)) {
          s.bahan.item_tambahan.forEach(it => {
            if (it.nama && !it.dari_client) {
              totalItems++;
              if (!checkPackagingInInventory('item_tambahan', it.nama, false)) missingCount++;
            }
          });
        }
      }
    });
    return { missingCount, totalItems };
  };

  // Mutations
  const delRacikanMut = useMutation({
    mutationFn: id => base44.entities.RacikanDatabase.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['racikan-database'] });
    }
  });

  const delBahanMut = useMutation({
    mutationFn: id => base44.entities.DatabaseBahan.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['database-bahan'] });
    }
  });

  const updateBahanImageMut = useMutation({
    mutationFn: ({ id, image_url }) => base44.entities.DatabaseBahan.update(id, { image_url }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['database-bahan'] });
    }
  });

  const toggleHakiMut = useMutation({
    mutationFn: async ({ brandName, currentStatus }) => {
      const bList = bahanList.filter(b => b.nama_brand === brandName);
      const rList = racikanList.filter(r => r.nama_brand === brandName);
      const promises = [];
      for (const b of bList) promises.push(base44.entities.DatabaseBahan.update(b.id, { is_haki: !currentStatus }));
      for (const r of rList) promises.push(base44.entities.RacikanDatabase.update(r.id, { is_haki: !currentStatus }));
      await Promise.all(promises);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['database-bahan'] });
      qc.invalidateQueries({ queryKey: ['racikan-database'] });
    }
  });

  const toggleBpomMut = useMutation({
    mutationFn: async ({ brandName, variantName, currentStatus }) => {
      const bList = bahanList.filter(b => b.nama_brand === brandName && b.nama_varian === variantName);
      const rList = racikanList.filter(r => r.nama_brand === brandName && r.nama_varian === variantName);
      const promises = [];
      for (const b of bList) promises.push(base44.entities.DatabaseBahan.update(b.id, { is_bpom: !currentStatus }));
      for (const r of rList) promises.push(base44.entities.RacikanDatabase.update(r.id, { is_bpom: !currentStatus }));
      await Promise.all(promises);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['database-bahan'] });
      qc.invalidateQueries({ queryKey: ['racikan-database'] });
    }
  });

  const updateBrandNameMut = useMutation({
    mutationFn: async ({ oldName, newName }) => {
      const bList = bahanList.filter(b => b.nama_brand === oldName);
      const rList = racikanList.filter(r => r.nama_brand === oldName);
      const promises = [];
      for (const b of bList) promises.push(base44.entities.DatabaseBahan.update(b.id, { nama_brand: newName }));
      for (const r of rList) promises.push(base44.entities.RacikanDatabase.update(r.id, { nama_brand: newName }));
      await Promise.all(promises);
    },
    onSuccess: (_, { newName }) => {
      qc.invalidateQueries({ queryKey: ['database-bahan'] });
      qc.invalidateQueries({ queryKey: ['racikan-database'] });
      if (selectedBrand) setSelectedBrand(newName);
    }
  });

  const updateVariantNameMut = useMutation({
    mutationFn: async ({ brandName, oldName, newName }) => {
      if (!newName || !newName.trim() || newName.trim() === oldName) return;
      const cleanNew = newName.trim();

      // 1. Update DatabaseBahan for all sizes of this variant
      const bList = bahanList.filter(b => (b.nama_brand || '').trim().toLowerCase() === brandName.trim().toLowerCase());
      const bPromises = [];
      for (const b of bList) {
        let vName = b.nama_varian || '';
        let shouldUpdate = false;
        let updatedVName = vName;

        if (vName === oldName) {
          updatedVName = cleanNew;
          shouldUpdate = true;
        } else if (vName.startsWith(oldName + ' || ')) {
          updatedVName = cleanNew + ' || ' + vName.split(' || ')[1];
          shouldUpdate = true;
        } else if (vName.startsWith(oldName + ' - Item ')) {
          updatedVName = cleanNew + ' - Item ' + vName.split(' - Item ')[1];
          shouldUpdate = true;
        }

        if (shouldUpdate) {
          bPromises.push(base44.entities.DatabaseBahan.update(b.id, { nama_varian: updatedVName }));
        }
      }

      // 2. Update RacikanDatabase for all sizes of this variant
      const rList = racikanList.filter(r => (r.nama_brand || '').trim().toLowerCase() === brandName.trim().toLowerCase());
      const rPromises = [];
      for (const r of rList) {
        let vName = r.nama_varian || '';
        let shouldUpdate = false;
        let updatedVName = vName;

        if (vName === oldName) {
          updatedVName = cleanNew;
          shouldUpdate = true;
        } else if (vName.startsWith(oldName + ' || ')) {
          updatedVName = cleanNew + ' || ' + vName.split(' || ')[1];
          shouldUpdate = true;
        } else if (vName.startsWith(oldName + ' - Item ')) {
          updatedVName = cleanNew + ' - Item ' + vName.split(' - Item ')[1];
          shouldUpdate = true;
        }

        if (shouldUpdate) {
          rPromises.push(base44.entities.RacikanDatabase.update(r.id, { nama_varian: updatedVName }));
        }
      }

      await Promise.all([...bPromises, ...rPromises]);
      return cleanNew;
    },
    onSuccess: (cleanNew) => {
      qc.invalidateQueries({ queryKey: ['database-bahan'] });
      qc.invalidateQueries({ queryKey: ['racikan-database'] });
      if (cleanNew) setSelectedVariant(cleanNew);
      setRenameVariantModal(null);
      toast.success(`✅ Nama varian berhasil diubah menjadi "${cleanNew}" untuk seluruh ukuran.`);
    },
    onError: (err) => {
      toast.error('Gagal mengubah nama varian: ' + err.message);
    }
  });

  const handleDirectUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeVariantData?.bahan?.id) return;

    setUploadingImage(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      await updateBahanImageMut.mutateAsync({ id: activeVariantData.bahan.id, image_url: file_url });
    } catch (err) {
      console.error(err);
      alert('Gagal mengunggah gambar.');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  // Merge unique brand names from both databases
  const brandsData = useMemo(() => {
    const map = {};

    // Group racikan
    racikanList.forEach(r => {
      const bName = r.nama_brand || 'No Brand';
      if (!map[bName]) map[bName] = { name: bName, variants: {}, is_haki: r.is_haki === true };
      if (r.is_haki) map[bName].is_haki = true;

      let vName = r.nama_varian || 'Unnamed';
      let sName = r.ukuran_ml ? String(r.ukuran_ml) : 'Unknown';
      let joinKey = sName;

      // Logic for Discovery Set grouping
      if (vName.includes(' || ')) {
        const parts = vName.split(' || ');
        vName = parts[0];
        sName = `${parts[1]} (${sName}ml)`;
        joinKey = parts[1]; // Use the sub-item name as the unique size key
      } else if (vName.includes(' - Item ')) {
        const parts = vName.split(' - Item ');
        vName = parts[0];
        sName = `Item ${parts[1]} (${sName}ml)`;
        joinKey = `Item ${parts[1]}`;
      }

      if (!map[bName].variants[vName]) {
        map[bName].variants[vName] = { name: vName, sizes: {}, updated: new Date(0), is_bpom: r.is_bpom === true };
      }
      if (r.is_bpom) map[bName].variants[vName].is_bpom = true;
      
      if (!map[bName].variants[vName].sizes[joinKey]) {
        map[bName].variants[vName].sizes[joinKey] = { size: sName, racikan: null, bahan: null };
      }
      map[bName].variants[vName].sizes[joinKey].racikan = r;

      const rDate = new Date(r.updated_date || r.created_date || 0);
      if (rDate > map[bName].variants[vName].updated) {
        map[bName].variants[vName].updated = rDate;
      }
    });

    // Group packaging/materials
    bahanList.forEach(b => {
      const bName = b.nama_brand || 'No Brand';
      if (!map[bName]) map[bName] = { name: bName, variants: {}, is_haki: b.is_haki === true };
      if (b.is_haki) map[bName].is_haki = true;

      let vName = b.nama_varian || 'Unnamed';
      let sName = b.botol_ukuran_label_ml ? String(b.botol_ukuran_label_ml) : 'Unknown';
      let joinKey = sName;

      // Logic for Discovery Set grouping
      if (vName.includes(' || ')) {
        const parts = vName.split(' || ');
        vName = parts[0];
        sName = `${parts[1]} (${sName}ml)`;
        joinKey = parts[1];
      } else if (vName.includes(' - Item ')) {
        const parts = vName.split(' - Item ');
        vName = parts[0];
        sName = `Item ${parts[1]} (${sName}ml)`;
        joinKey = `Item ${parts[1]}`;
      }

      if (!map[bName].variants[vName]) {
        map[bName].variants[vName] = { name: vName, sizes: {}, updated: new Date(0), is_bpom: b.is_bpom === true };
      }
      if (b.is_bpom) map[bName].variants[vName].is_bpom = true;
      
      if (!map[bName].variants[vName].sizes[joinKey]) {
        map[bName].variants[vName].sizes[joinKey] = { size: sName, racikan: null, bahan: null };
      }
      map[bName].variants[vName].sizes[joinKey].bahan = b;

      const bDate = new Date(b.updated_date || b.created_date || 0);
      if (bDate > map[bName].variants[vName].updated) {
        map[bName].variants[vName].updated = bDate;
      }
    });

    // Convert variants map to array and compute latest update per brand
    return Object.values(map).map(brand => {
      const variantList = Object.values(brand.variants).map(v => {
        return {
          ...v,
          sizes: Object.values(v.sizes).sort((a, b) => {
            const na = parseFloat(a.size) || 0;
            const nb = parseFloat(b.size) || 0;
            return na - nb;
          })
        };
      });
      variantList.sort((a, b) => a.name.localeCompare(b.name, 'id', { sensitivity: 'base' }));
      const latestUpdate = variantList.reduce((max, cur) => cur.updated > max ? cur.updated : max, new Date(0));
      return {
        ...brand,
        variants: variantList,
        latestUpdate,
      };
    });
  }, [racikanList, bahanList]);

  // Filtered and Sorted Brand list
  const filteredBrands = useMemo(() => {
    let list = [...brandsData];

    // Search query
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(b => {
        if (b.name.toLowerCase().includes(q)) return true;
        return b.variants.some(v => {
          if (v.name.toLowerCase().includes(q)) return true;
          return v.sizes.some(s => {
            if (s.racikan && Array.isArray(s.racikan.racikan)) {
              return s.racikan.racikan.some(item => 
                item.nama_bahan && item.nama_bahan.toLowerCase().includes(q)
              );
            }
            return false;
          });
        });
      });
    }

    // Sort options
    if (sort === 'az') {
      list.sort((a, b) => a.name.localeCompare(b.name, 'id'));
    } else if (sort === 'za') {
      list.sort((a, b) => b.name.localeCompare(a.name, 'id'));
    } else if (sort === 'terbaru') {
      list.sort((a, b) => b.latestUpdate - a.latestUpdate);
    }

    return list;
  }, [brandsData, search, sort]);

  const activeBrand = useMemo(() => {
    if (!selectedBrand) return null;
    return brandsData.find(b => b.name === selectedBrand) || null;
  }, [brandsData, selectedBrand]);

  const activeVariantData = useMemo(() => {
    if (!activeBrand || !selectedVariant) return null;
    return activeBrand.variants.find(v => v.name === selectedVariant) || null;
  }, [activeBrand, selectedVariant]);

  const handleDeleteVariant = (variant) => {
    if (!confirm(`Hapus seluruh data varian ${variant.name} beserta semua ukurannya? Ini akan menghapus seluruh data racikan & bahan packaging.`)) return;
    variant.sizes.forEach(s => {
      if (s.racikan?.id) delRacikanMut.mutate(s.racikan.id, { onSuccess: () => qc.invalidateQueries({ queryKey: ['racikan-database'] }) });
      if (s.bahan?.id) delBahanMut.mutate(s.bahan.id, { onSuccess: () => qc.invalidateQueries({ queryKey: ['database-bahan'] }) });
    });
    setSelectedVariant(null);
  };

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection
  } = useBatchSelection(filteredBrands, b => b.name);

  const handleBatchDeleteBrands = async () => {
    if (!confirm(`Hapus ${selectedIds.size} brand yang dipilih beserta SEMUA varian dan ukurannya?`)) return;
    
    const racikanToDelete = [];
    const bahanToDelete = [];
    
    brandsData.forEach(brand => {
      if (selectedIds.has(brand.name)) {
        brand.variants.forEach(v => {
          v.sizes.forEach(s => {
            if (s.racikan?.id) racikanToDelete.push(s.racikan.id);
            if (s.bahan?.id) bahanToDelete.push(s.bahan.id);
          });
        });
      }
    });

    try {
      await Promise.all([
        ...racikanToDelete.map(id => delRacikanMut.mutateAsync(id)),
        ...bahanToDelete.map(id => delBahanMut.mutateAsync(id))
      ]);
      qc.invalidateQueries({ queryKey: ['racikan-database'] });
      qc.invalidateQueries({ queryKey: ['database-bahan'] });
      clearSelection();
    } catch (err) {
      console.error(err);
      alert('Terjadi kesalahan saat menghapus brand.');
    }
  };

  const isLoading = loadingRacikan || loadingBahan;

  const brandStats = useMemo(() => {
    let totalBrands = brandsData.length;
    let totalVariants = 0;
    let integratedBrands = 0;
    let pendingBrands = 0;

    brandsData.forEach(brand => {
      totalVariants += brand.variants.length;
      const { missingCount, totalItems } = getBrandMissingCount(brand);
      if (missingCount > 0) {
        pendingBrands++;
      } else if (totalItems > 0) {
        integratedBrands++;
      }
    });

    return { totalBrands, totalVariants, integratedBrands, pendingBrands };
  }, [brandsData, masterBahanList, botolList, tutupList, sprayList, itemTambahanList, fullsetList]);

  return (
    <div className="space-y-6 text-[#302621]">
      {/* Page Header */}
      <div className="flex justify-between items-center flex-wrap gap-2 md:gap-4">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-xl md:text-3xl font-black text-slate-800 tracking-tight">Database Brand</h1>
            <span className="text-xs font-bold bg-[#F9F5EC] text-[#8C6208] border border-[#E6D5B8] px-2.5 py-1 rounded-full shadow-2xs">
              {brandStats.totalBrands} Brand Terdaftar
            </span>
          </div>
          <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1">Gabungan pangkalan data formula racikan cairan serta material kemasan / packaging tiap produk brand.</p>
          <div className="text-[9px] md:text-[11px] text-[#8C6208] bg-[#F9F5EC] border border-[#E6D5B8]/40 px-2 md:px-3 py-1.5 md:py-2 rounded-lg md:rounded-xl mt-2 font-medium inline-flex items-center gap-1.5 shadow-sm">
            <span>💡</span>
            <span><strong>Tip:</strong> Untuk mendaftarkan brand baru, klik <strong>Formula Baru</strong> atau <strong>Kemasan Baru</strong> di samping dan isi nama brand baru Anda. Brand akan terdaftar otomatis.</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button onClick={() => { 
                setEditRacikanItem(null); 
                setEditBahanItem(null); 
                setShowBrandProdukForm(true); 
              }} className="bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] hover:from-[#AA7C11] hover:to-[#8C6208] text-white font-bold gap-2 text-xs rounded-xl shadow-md">
              <Plus className="w-4 h-4" /> Produk / Varian Baru
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : !selectedBrand ? (
        /* Brand Card List (Main Dashboard View) */
        <div className="space-y-6">
          {/* Quick Summary Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-[#E6D5B8]/60 p-3.5 rounded-2xl shadow-xs">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Brand</p>
              <p className="text-xl font-black text-[#8C6208] mt-0.5">{brandStats.totalBrands} <span className="text-xs font-semibold text-slate-400">Brand</span></p>
            </div>
            <div className="bg-white border border-[#E6D5B8]/60 p-3.5 rounded-2xl shadow-xs">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Varian</p>
              <p className="text-xl font-black text-slate-800 mt-0.5">{brandStats.totalVariants} <span className="text-xs font-semibold text-slate-400">Varian</span></p>
            </div>
            <div className="bg-white border border-emerald-200/80 bg-emerald-50/20 p-3.5 rounded-2xl shadow-xs">
              <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
                <span>✓</span> Terintegrasi
              </p>
              <p className="text-xl font-black text-emerald-700 mt-0.5">{brandStats.integratedBrands} <span className="text-xs font-semibold text-emerald-600/70">Brand</span></p>
            </div>
            <div className="bg-white border border-rose-200/80 bg-rose-50/20 p-3.5 rounded-2xl shadow-xs">
              <p className="text-[10px] font-bold text-rose-700 uppercase tracking-wider flex items-center gap-1">
                <span>●</span> Perlu Integrasi
              </p>
              <p className="text-xl font-black text-rose-700 mt-0.5">{brandStats.pendingBrands} <span className="text-xs font-semibold text-rose-600/70">Brand</span></p>
            </div>
          </div>

          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Cari nama brand atau varian produk..." 
                className="pl-9 pr-3 py-2 border border-[#E6D5B8] bg-white rounded-xl text-xs" 
                value={search} 
                onChange={e => setSearch(e.target.value)} 
              />
            </div>
            <SortDropdown value={sort} onChange={setSort} />
          </div>

          {filteredBrands.length === 0 ? (
            <div className="text-center py-20 bg-white border border-slate-200 rounded-3xl">
              <Database className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-semibold text-slate-500">Belum ada database brand terdaftar.</p>
              <p className="text-xs text-slate-400 mt-1">Gunakan tombol diatas untuk mulai membuat formula / material brand baru.</p>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {filteredBrands.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(brand => {
                const totalVariants = brand.variants.length;
                let hasRacikanCount = 0;
                let hasBahanCount = 0;
                brand.variants.forEach(v => {
                  v.sizes.forEach(s => {
                    if (s.racikan) hasRacikanCount++;
                    if (s.bahan) hasBahanCount++;
                  });
                });

                return (
                  <button
                    key={brand.name}
                    onClick={() => { 
                      if (isSelectionMode) {
                        toggleSelection(brand.name);
                      } else {
                        setSelectedBrand(brand.name); setSelectedVariant(null); setVariantSearch('');
                      }
                    }}
                    className={`text-left bg-white border ${isSelectionMode && selectedIds.has(brand.name) ? 'border-rose-500 bg-rose-50/20 shadow-md ring-2 ring-rose-500/20' : 'border-[#E6D5B8]/60 hover:border-[#D4AF37]'} rounded-xl md:rounded-2xl p-3 md:p-5 hover:shadow-lg transition-all group relative overflow-hidden`}
                  >
                    <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                      {canEdit && !isSelectionMode && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="w-8 h-8 text-blue-500/40 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setRenameBrand(brand.name);
                              setNewBrandName(brand.name);
                            }}
                            disabled={updateBrandNameMut.isPending}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="w-8 h-8 text-rose-500/40 hover:text-rose-600 hover:bg-rose-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                            onClick={(e) => { e.stopPropagation(); startSelection(brand.name); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {isSelectionMode && (
                        <div 
                          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${selectedIds.has(brand.name) ? 'bg-rose-500 border-rose-500 text-white' : 'border-slate-300 bg-white'}`}
                        >
                          {selectedIds.has(brand.name) && <svg viewBox="0 0 24 24" fill="none" className="w-3.5 h-3.5 stroke-current stroke-[3]"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                        </div>
                      )}
                    </div>
                    
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#F9F5EC] to-transparent rounded-bl-full -z-10 group-hover:scale-110 transition-transform"></div>
                    <div className="flex items-center justify-between mb-2 md:mb-4">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-[#F9F5EC] border border-[#E6D5B8]/50 flex items-center justify-center">
                        <Award className="w-4 h-4 md:w-5 md:h-5 text-[#8C6208]" />
                      </div>
                      {(() => {
                        const { missingCount, totalItems } = getBrandMissingCount(brand);
                        if (missingCount > 0) {
                          return (
                            <span className="text-[8px] md:text-[9.5px] bg-rose-50 text-rose-700 border border-rose-200 font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 shadow-2xs mr-8" title={`Ada ${missingCount} item pada brand ini yang belum terintegrasi dengan inventori`}>
                              ● {missingCount} Blm Terintegrasi
                            </span>
                          );
                        } else if (totalItems > 0) {
                          return (
                            <span className="text-[8px] md:text-[9.5px] bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 mr-8">
                              ✓ Terintegrasi
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <h3 className="font-bold text-slate-800 text-[11px] md:text-sm tracking-tight truncate group-hover:text-[#8C6208] transition-colors">{brand.name}</h3>
                    <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1 font-semibold">{totalVariants} Varian Produk</p>
                    
                    <div className="flex items-center gap-2 md:gap-2.5 mt-2 md:mt-3 text-[9px] md:text-[10px] text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1 font-semibold text-[#8C6208]">
                        <Layers className="w-2.5 h-2.5 md:w-3 md:h-3 text-[#8C6208]" /> {totalVariants} varian
                      </span>
                      <span className="w-1 h-1 bg-slate-300 rounded-full" />
                      <span className="flex items-center gap-1">
                        <FlaskConical className="w-2.5 h-2.5 md:w-3 md:h-3 text-[#8C6208]" /> {hasRacikanCount} formula
                      </span>
                      <span className="w-1 h-1 bg-slate-300 rounded-full" />
                      <span className="flex items-center gap-1">
                        <Package className="w-2.5 h-2.5 md:w-3 md:h-3 text-slate-600" /> {hasBahanCount} kemasan
                      </span>
                    </div>

                    <div className="mt-3 md:mt-4 pt-2 md:pt-3 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-[8px] md:text-[10px] text-slate-400">Update: {brand.latestUpdate.toLocaleDateString('id-ID')}</span>
                      <span className="text-[10px] md:text-xs font-black text-[#8C6208] flex items-center gap-0.5 group-hover:translate-x-1 transition-transform">
                        Lihat <ChevronRight className="w-2.5 h-2.5 md:w-3 md:h-3" />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            
          {/* ── Pagination Controls ── */}
          {filteredBrands.length > 0 && (() => {
            const totalPages = Math.ceil(filteredBrands.length / itemsPerPage);
            const startIdx = (currentPage - 1) * itemsPerPage + 1;
            const endIdx = Math.min(currentPage * itemsPerPage, filteredBrands.length);

            return (
              <div className="px-5 py-3.5 border border-[#E6D5B8]/60 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6 shadow-xs">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">Tampilkan:</span>
                    <RowPerPageDropdown 
                      value={itemsPerPage} 
                      onChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }} 
                    />
                  </div>
                  <span className="text-xs font-semibold text-slate-400">
                    Menampilkan <strong className="text-slate-700 font-bold">{startIdx} - {endIdx}</strong> dari <strong className="text-slate-700 font-bold">{filteredBrands.length}</strong> Brand
                  </span>
                </div>
                
                <div className="flex items-center gap-1.5">
                  <button 
                    onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); scrollToTop(); }}
                    disabled={currentPage === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-xl text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208] border border-transparent hover:border-[#E6D5B8] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-transparent disabled:hover:text-slate-400 transition-all font-bold"
                    title="Halaman Sebelumnya"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  {(() => {
                    const pages = [];
                    for(let i=1; i<=totalPages; i++) {
                      if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => { setCurrentPage(i); scrollToTop(); }}
                            className={`w-8 h-8 flex items-center justify-center rounded-xl text-xs font-black transition-all ${
                              currentPage === i 
                                ? 'bg-gradient-to-r from-[#D4AF37] to-[#8C6208] text-white shadow-md shadow-[#8C6208]/20' 
                                : 'text-slate-600 hover:bg-[#F9F5EC] hover:text-[#8C6208] border border-transparent hover:border-[#E6D5B8]/60'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      } else if (i === currentPage - 2 || i === currentPage + 2) {
                        pages.push(<span key={i} className="text-slate-400 text-xs px-1">...</span>);
                      }
                    }
                    return pages;
                  })()}
                  
                  <button 
                    onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); scrollToTop(); }}
                    disabled={currentPage >= totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-xl text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208] border border-transparent hover:border-[#E6D5B8] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-transparent disabled:hover:text-slate-400 transition-all font-bold"
                    title="Halaman Berikutnya"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })()}
          </>
          )}
        </div>
      ) : selectedBrand && activeBrand ? (
        /* Brand Profile View & Merged Variant Details */
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-[#E6D5B8]/50 gap-4">
            <button 
              onClick={() => { setSelectedBrand(null); setSelectedVariant(null); setVariantSearch(''); }}
              className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors bg-white px-3 py-1.5 rounded-lg border border-slate-200"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke Database
            </button>
            <div className="flex items-center gap-2">
              {isMarketing ? (
                <button 
                  onClick={() => toggleHakiMut.mutate({ brandName: activeBrand.name, currentStatus: activeBrand.is_haki })}
                  className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full border shadow-sm transition-colors ${activeBrand.is_haki ? 'bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200'}`}
                >
                  {toggleHakiMut.isPending ? '...' : (activeBrand.is_haki ? '✓ Sudah HAKI' : '✗ Belum HAKI')}
                </button>
              ) : (
                <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full border shadow-sm ${activeBrand.is_haki ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-500 border-slate-300'}`}>
                  {activeBrand.is_haki ? '✓ Sudah HAKI' : '✗ Belum HAKI'}
                </span>
              )}
              <span className="text-xs font-black text-[#8C6208] uppercase tracking-wider bg-[#F9F5EC] border border-[#E6D5B8]/60 px-3 py-1 rounded-full">
                Brand: {activeBrand.name}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAsaExportModal(true)}
                className="h-8 border-[#8C6208] text-[#8C6208] hover:bg-[#F9F5EC] rounded-full text-xs font-bold px-3.5 flex items-center gap-1.5 shadow-2xs cursor-pointer transition-all hover:shadow-xs"
                title="Buka Dokumen PDF A4 Daftar Harga & Jasa Produk"
              >
                <FileText className="w-3.5 h-3.5" />
                <span>Export PDF Daftar Harga</span>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Side: Variants List */}
            <div className="lg:col-span-4 space-y-3">
              <div className="bg-white border border-[#E6D5B8]/60 p-4 rounded-2xl">
                <p className="text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Daftar Varian Produk</p>
                
                {/* Variant Search Bar */}
                <div className="relative mb-3">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input 
                    placeholder="Cari varian..." 
                    className="pl-8 py-1.5 h-8 border border-[#E6D5B8] bg-[#FFFDF9] rounded-lg text-xs" 
                    value={variantSearch} 
                    onChange={e => setVariantSearch(e.target.value)} 
                  />
                </div>

                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {activeBrand.variants
                    .filter(variant => {
                      const q = variantSearch.toLowerCase();
                      if (variant.name.toLowerCase().includes(q)) return true;
                      return variant.sizes.some(s => {
                        if (s.racikan && Array.isArray(s.racikan.racikan)) {
                          return s.racikan.racikan.some(item => 
                            item.nama_bahan && item.nama_bahan.toLowerCase().includes(q)
                          );
                        }
                        return false;
                      });
                    })
                    .map(variant => {
                      const isActive = selectedVariant === variant.name;
                      return (
                        <div
                          key={variant.name}
                          onClick={() => setSelectedVariant(variant.name)}
                          className={`w-full group text-left p-3.5 rounded-xl border transition-all flex items-center justify-between cursor-pointer ${
                            isActive 
                              ? 'bg-[#F9F5EC] border-[#E6D5B8] text-[#8C6208] shadow-sm'
                              : 'bg-white border-slate-200 hover:border-[#E6D5B8] text-slate-700'
                          }`}
                        >
                          <div className="min-w-0 flex-1 mr-2">
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-black truncate">{variant.name}</p>
                              {canEdit && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRenameVariantModal({ oldName: variant.name, newName: variant.name });
                                  }}
                                  className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-[#8C6208] text-slate-400 rounded transition-opacity"
                                  title="Ubah Nama Varian"
                                >
                                  <Edit3 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                            {variant.sizes && variant.sizes.length > 0 && (
                              <p className="text-[10px] text-slate-400 font-semibold mt-0.5">
                                Ukuran: {variant.sizes.map(s => {
                                  const sz = String(s.size);
                                  return sz.toLowerCase().includes('ml') ? sz : `${sz}ml`;
                                }).join(', ')}
                              </p>
                            )}
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {variant.sizes.some(s => s.racikan) && <Badge variant="outline" className="text-[9px] bg-amber-50 text-[#8C6208] border-amber-200 font-bold">Formula</Badge>}
                              {variant.sizes.some(s => s.bahan) && <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200 font-bold">Kemasan</Badge>}
                              {(() => {
                                const { missingCount: vMissing, totalItems: vTotal } = getVariantMissingCount(variant);
                                if (vMissing > 0) {
                                  return (
                                    <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200 font-bold">
                                      ● {vMissing} Blm Terintegrasi
                                    </Badge>
                                  );
                                } else if (vTotal > 0) {
                                  return (
                                    <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 font-bold">
                                      ✓ Terintegrasi
                                    </Badge>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                          <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${isActive ? 'translate-x-1 text-[#8C6208]' : 'text-slate-400'}`} />
                        </div>
                      );
                  })}
                </div>
              </div>
            </div>

            {/* Right Side: Variant Detail View (Merged Formula + Kemasan, Multi-Size) */}
            <div className="lg:col-span-8 space-y-4">
              {!selectedVariant ? (
                <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center flex flex-col justify-center items-center h-full min-h-[300px]">
                  <FlaskConical className="w-12 h-12 text-slate-300 mb-3 animate-pulse" />
                  <p className="text-sm font-bold text-slate-500">Pilih varian di samping.</p>
                  <p className="text-xs text-slate-400 mt-1">Untuk melihat formula cairan, botol, tutup, spray & kelengkapan aksesorisnya.</p>
                </div>
              ) : activeVariantData && (
                <div className="space-y-6">
                  {/* Variant Header Info Card */}
                  <Card className="border border-[#E6D5B8] shadow-sm overflow-hidden mb-6">
                    <CardContent className="p-6 bg-gradient-to-r from-white to-[#F9F5EC]/20 relative">
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <h2 className="text-xl font-black text-slate-800 tracking-tight">{activeVariantData.name}</h2>
                            {canEdit && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="w-7 h-7 text-slate-400 hover:text-[#8C6208] hover:bg-[#F9F5EC] rounded-lg transition-colors cursor-pointer"
                                onClick={() => setRenameVariantModal({ oldName: activeVariantData.name, newName: activeVariantData.name })}
                                title="Ubah Nama Varian untuk Seluruh Ukuran Sekaligus"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {isMarketing ? (
                              <button 
                                onClick={() => toggleBpomMut.mutate({ brandName: activeBrand.name, variantName: activeVariantData.name, currentStatus: activeVariantData.is_bpom })}
                                className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded border shadow-sm transition-colors ${activeVariantData.is_bpom ? 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200' : 'bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200'}`}
                              >
                                {toggleBpomMut.isPending ? '...' : (activeVariantData.is_bpom ? '✓ BPOM' : '✗ Belum BPOM')}
                              </button>
                            ) : (
                              <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded border shadow-sm ${activeVariantData.is_bpom ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-slate-100 text-slate-500 border-slate-300'}`}>
                                {activeVariantData.is_bpom ? '✓ BPOM' : '✗ Belum BPOM'}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#8C6208] font-bold mt-1 uppercase tracking-wider">{activeBrand.name}</p>
                          <p className="text-xs text-slate-500 mt-2">{activeVariantData.sizes.length} Ukuran Terdaftar</p>
                        </div>
                        {canEdit && (
                          <div className="flex flex-col gap-2">
                            {activeVariantData.name === 'Discovery Set' && (
                              <Button 
                                variant="default" 
                                size="sm" 
                                onClick={() => {
                                  const allSizes = activeVariantData.sizes;
                                  const packagingEntry = allSizes.find(s => {
                                    const itemName = s.racikan?.nama_varian || s.bahan?.nama_varian || '';
                                    return itemName.includes(' || Packaging / Box');
                                  });

                                  const dsItems = allSizes
                                    .filter(s => s !== packagingEntry)
                                    .map((s, idx) => {
                                      const r = s.racikan || {};
                                      const b = s.bahan || {};
                                      
                                      const itemName = r.nama_varian || b.nama_varian || '';
                                      const itemTitle = itemName.includes(' || ') ? itemName.split(' || ')[1] : `Item ${idx+1}`;
                                      
                                      const isFullset = b.is_fullset || false;
                                      
                                      return {
                                        _racikan_id: r.id,
                                        _bahan_id: b.id,
                                        nama_variant: itemTitle,
                                        is_fullset: isFullset,
                                        _original_racikan: r,
                                        _original_bahan: b
                                      };
                                    });
                                  
                                  const itemTambahan = packagingEntry?.bahan?.item_tambahan || [];
                                  const packagingIds = packagingEntry ? { racikan_id: packagingEntry.racikan?.id, bahan_id: packagingEntry.bahan?.id } : null;

                                  setEditDiscoverySetData({ items: dsItems, item_tambahan: itemTambahan, packaging_ids: packagingIds, nama_brand: activeBrand.name, nama_varian: activeVariantData.name, pic: activeVariantData.sizes[0]?.racikan?.pic });
                                  setShowBrandProdukForm(true);
                                }}
                                className="bg-[#8C6208] hover:bg-[#7A5506] text-white h-8 text-xs font-bold rounded-lg shadow-sm w-full"
                              >
                                Edit Keseluruhan Set
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleDeleteVariant(activeVariantData)}
                              className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 gap-1.5 h-8 text-xs font-bold rounded-lg w-full"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Hapus Varian Ini
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Daftar Accordion per Ukuran */}
                  <div className="space-y-4">
                    {activeVariantData.sizes.length > 0 ? (
                      activeVariantData.sizes.map(sizeData => (
                        <VariantSizeAccordion
                          key={sizeData.size}
                          sizeData={sizeData}
                          activeBrandName={activeBrand.name}
                          variantName={activeVariantData.name}
                          canEdit={canEdit}
                          isMarketing={isMarketing}
                          fileRef={fileRef}
                          onEditVariant={(sizeData) => { 
                            if (activeVariantData.name === 'Discovery Set') {
                              const allSizes = activeVariantData.sizes;
                              const packagingEntry = allSizes.find(s => {
                                const itemName = s.racikan?.nama_varian || s.bahan?.nama_varian || '';
                                return itemName.includes(' || Packaging / Box');
                              });

                              const dsItems = allSizes
                                .filter(s => s !== packagingEntry)
                                .map((s, idx) => {
                                  const r = s.racikan || {};
                                  const b = s.bahan || {};
                                  const itemName = r.nama_varian || b.nama_varian || '';
                                  const itemTitle = itemName.includes(' || ') ? itemName.split(' || ')[1] : `Item ${idx+1}`;
                                  return {
                                    _racikan_id: r.id,
                                    _bahan_id: b.id,
                                    nama_variant: itemTitle,
                                    is_fullset: b.is_fullset || false,
                                    _original_racikan: r,
                                    _original_bahan: b
                                  };
                                });
                                
                              const itemTambahan = packagingEntry?.bahan?.item_tambahan || [];
                              const packagingIds = packagingEntry ? { racikan_id: packagingEntry.racikan?.id, bahan_id: packagingEntry.bahan?.id } : null;

                              setEditDiscoverySetData({ items: dsItems, item_tambahan: itemTambahan, packaging_ids: packagingIds, nama_brand: activeBrand.name, nama_varian: activeVariantData.name, pic: activeVariantData.sizes[0]?.racikan?.pic });
                              setShowBrandProdukForm(true);
                            } else {
                              setEditRacikanItem(sizeData.racikan || null); 
                              setEditBahanItem(sizeData.bahan || null); 
                              setShowBrandProdukForm(true); 
                            }
                          }}
                          onDeleteRacikan={(id) => delRacikanMut.mutate(id)}
                          onDeleteBahan={(id) => delBahanMut.mutate(id)}
                        />
                      ))
                    ) : (
                      <div className="text-center py-10">
                        <p className="text-sm text-slate-500">Belum ada ukuran terdaftar untuk varian ini.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Batch Action Bar */}
      <BatchActionBar 
        isSelectionMode={isSelectionMode}
        selectedCount={selectedIds.size}
        totalCount={filteredBrands.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={handleBatchDeleteBrands}
        isDeleting={delRacikanMut.isPending || delBahanMut.isPending}
      />

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {showBrandProdukForm && (
        <BrandProdukForm
          racikanItem={editRacikanItem}
          bahanItem={editBahanItem}
          initialDiscoverySetData={editDiscoverySetData}
          initialBrand={activeBrand?.name || ''}
          initialVarian={activeVariantData?.name || ''}
          copyFromVariant={activeVariantData}
          isMarketing={isMarketing}
          onClose={() => { 
            setShowBrandProdukForm(false); 
            setEditRacikanItem(null); 
            setEditBahanItem(null); 
            setEditDiscoverySetData(null); 
          }}
          onSaved={() => {
            setShowBrandProdukForm(false);
            setEditRacikanItem(null);
            setEditBahanItem(null);
          }}
        />
      )}

      {/* Dialog Ubah Nama Brand */}
      <Dialog open={!!renameBrand} onOpenChange={(open) => { if (!open) setRenameBrand(null); }}>
        <DialogContent className="max-w-md border-[#E6D5B8] bg-[#FFFDF9] rounded-2xl shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-base md:text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
              <Award className="w-5 h-5 text-[#8C6208]" />
              Ubah Nama Brand
            </DialogTitle>
            <DialogDescription className="text-[11px] md:text-xs text-slate-500">
              Mengubah nama brand akan memperbarui seluruh formula dan kemasan yang terdaftar di bawah brand ini.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 md:py-4">
            <div className="space-y-2">
              <label className="text-[11px] md:text-xs font-bold text-slate-700">Nama Brand Baru</label>
              <Input
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                placeholder="Masukkan nama brand..."
                className="border-[#E6D5B8] focus:border-[#D4AF37] focus:ring-[#D4AF37]/10 bg-white rounded-xl text-xs py-2"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newBrandName.trim() && newBrandName.trim() !== renameBrand) {
                    handleRenameSubmit();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-2">
            <Button
              variant="outline"
              onClick={() => setRenameBrand(null)}
              className="border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-bold"
            >
              Batal
            </Button>
            <Button
              onClick={handleRenameSubmit}
              disabled={updateBrandNameMut.isPending || !newBrandName.trim() || newBrandName.trim() === renameBrand}
              className="bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] hover:from-[#AA7C11] hover:to-[#8C6208] text-white font-bold text-xs rounded-xl shadow-md"
            >
              {updateBrandNameMut.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Rename Varian (Sekaligus Seluruh Ukuran) */}
      <Dialog open={!!renameVariantModal} onOpenChange={(open) => !open && setRenameVariantModal(null)}>
        <DialogContent className="max-w-md bg-white rounded-3xl p-6 border border-[#E6D5B8] shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-[#F9F5EC] border border-[#E6D5B8] flex items-center justify-center text-[#8C6208]">
                <Edit3 className="w-4 h-4" />
              </div>
              Ubah Nama Varian
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 mt-1 leading-relaxed">
              Mengubah nama varian <strong>{renameVariantModal?.oldName}</strong> pada brand <strong>{activeBrand?.name}</strong> akan langsung diterapkan ke <strong>seluruh {activeVariantData?.sizes?.length || 0} ukuran</strong> (10ml, 30ml, 50ml, 100ml, dsb) secara serentak.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={(e) => {
            e.preventDefault();
            if (renameVariantModal && renameVariantModal.newName.trim()) {
              updateVariantNameMut.mutate({
                brandName: activeBrand.name,
                oldName: renameVariantModal.oldName,
                newName: renameVariantModal.newName.trim()
              });
            }
          }} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Nama Varian Baru</label>
              <Input
                value={renameVariantModal?.newName || ''}
                onChange={(e) => setRenameVariantModal({ ...renameVariantModal, newName: e.target.value })}
                placeholder="Contoh: Gentleman Black, Acua Kiss..."
                className="h-10 text-sm font-semibold rounded-xl border-[#E6D5B8] focus-visible:ring-[#8C6208]"
                autoFocus
              />
            </div>

            <div className="bg-amber-50/80 border border-amber-200/90 rounded-2xl p-3.5 text-[11px] text-amber-900 leading-relaxed flex items-start gap-2">
              <span className="shrink-0 text-base">🛡️</span>
              <div>
                <strong>Aman & Terisolasi:</strong> Seluruh invoice, PO, dan rekap keuangan masa lalu <strong>TIDAK AKAN BERUBAH</strong> karena invoice menyimpan data snapshot mandiri pada saat transaksi dibuat.
              </div>
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setRenameVariantModal(null)}
                className="h-10 text-xs rounded-xl"
              >
                Batal
              </Button>
              <Button
                type="submit"
                disabled={updateVariantNameMut.isPending || !renameVariantModal?.newName?.trim() || renameVariantModal?.newName?.trim() === renameVariantModal?.oldName}
                className="h-10 text-xs font-bold bg-[#8C6208] hover:bg-[#724F06] text-white rounded-xl shadow-md px-5"
              >
                {updateVariantNameMut.isPending ? 'Menyimpan...' : 'Simpan Nama Baru (Semua Ukuran)'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pop Up ASA untuk Pilih Varian Sebelum Export PDF */}
      {activeBrand && (
        <AsaExportBrandPdfModal
          isOpen={showAsaExportModal}
          onClose={() => setShowAsaExportModal(false)}
          activeBrand={activeBrand}
          botolList={botolList}
          tutupList={tutupList}
          sprayList={sprayList}
          masterBahanList={masterBahanList}
          itemTambahanList={itemTambahanList}
          fullsetList={fullsetList}
        />
      )}
    </div>
  );
}
